import React, { Fragment, useState } from 'react'
import { gql, useMutation } from '@apollo/client'
import PropTypes from 'prop-types'
import { Formik } from 'formik'
import { isEmpty } from 'lodash'
import { Helmet } from 'react-helmet'
import Typography from '@material-ui/core/Typography'
import Container from '@material-ui/core/Container'
import DialogAPIMessage from './profile-components/dialogAPIMessage'
import { initForm, editValidationSchema } from './constants'
import Navigation from './profile-components/navigation'
import useStyles from './profile.styles'
import { submissionHandler } from './utils'
import SaveCancelButtons from './profile-components/SaveCancelButtons'
import ErrorDisplay from './ErrorDisplay'
import FormPersistence from '../coaches-profile/FormPersistence'
import WebAddress from './profile-components/webaddress'
import Profile from './profile-components/profile'
import Testimonials from './profile-components/testimonials'
import Photos from './profile-components/photos'
import Facilities from './profile-components/facilities'
import Experience from './profile-components/experience'
import Services from './profile-components/services'
import Skills from './profile-components/skills'
import Tools from './profile-components/tools'
import ContentSection from 'components/content-section'

const UPDATE_USER = gql`
mutation CoachProfileUpdate($coachProfileId: ID!, $params: CoachProfileUpdateInput!) {
  CoachProfileUpdate(coachProfileId: $coachProfileId, params: $params) {
    message
    success
  }
}`

const CREATE_USER = gql`
mutation CoachProfileCreate($params: CoachProfileCreateInput!) {
  CoachProfileCreate(params: $params){
    message
    success
  }
}`

const isPositiveInt = num => !Number.isNaN(num) && num > 0

const validate = (slugError = null) => (values) => {
  // set the error to the value passed in
  const errors = {}
  if (slugError) {
    errors.customSlug = slugError
  }

  // Check if the slug value is empty
  if (values.customSlug === null || isEmpty(values.customSlug)) {
    errors.customSlug = 'You must have a unique, profile name for your page.'
  }
  // Check if the slug is longer than 25 characters
  if (values.customSlug.length > 25) {
    errors.customSlug = 'URL length can be up to 25 characters.'
  }

  if (values.numberOfFollowers && !isPositiveInt(values.numberOfFollowers)) {
    errors.numberOfFollowers = 'Value must be a positive integer'
  }

  if (values.numberOfStudents && !isPositiveInt(values.numberOfStudents)) {
    errors.numberOfStudents = 'Value must be a positive integer'
  }

  return errors
}

const ProfileForm = ({ formFields, newProfile }) => {
  const [slugError, setSlugError] = useState(null)
  // dialog state
  const [openApiResponseDialog, setOpenApiResponseDialog] = React.useState(false)
  const [apiResponseObject, setApiResponseObject] = useState()
  const handleDialogClose = () => setOpenApiResponseDialog(false)
  const classes = useStyles()

  const [updateOrCreateProfile] = useMutation(newProfile ? CREATE_USER : UPDATE_USER)

  const profileSectionsConfig = [
    {
      id: 'web-address',
      label: 'Profile Name',
      Component: WebAddress,
    },
    {
      id: 'profile',
      label: 'Profile',
      Component: Profile,
    },
    {
      id: 'testimonials',
      label: 'Testimonials',
      Component: Testimonials,
    },
    {
      id: 'photos',
      label: 'Photos',
      Component: Photos,
    },
    {
      id: 'facilities',
      label: 'Facilities',
      Component: Facilities,
    },
    {
      id: 'experience',
      label: 'Experience',
      Component: Experience,
    },
    {
      id: 'services',
      label: 'Services',
      Component: Services,
    },
    {
      id: 'skills',
      label: 'Skills',
      Component: Skills,
    },
    {
      id: 'tools',
      label: 'Tools',
      Component: Tools,
    },
  ]

  return (
    <>
      <Helmet>
        <script
          src="https://widget.cloudinary.com/v2.0/global/all.js"
          type="text/javascript"
        />
      </Helmet>
      <Formik
        initialValues={initForm(formFields)}
        onSubmit={values => (
          submissionHandler(
            values,
            newProfile,
            updateOrCreateProfile,
            formFields && formFields.coachProfileId,
            setApiResponseObject,
            setOpenApiResponseDialog,
          ))
        }
        validationSchema={editValidationSchema}
        validate={validate(slugError)}
      >
        {({
          values,
          touched,
          errors,
          isSubmitting,
          handleChange,
          handleBlur,
          handleSubmit,
          handleReset,
          setFieldValue,
          setValues,
        }) => (
          <div className={classes.root}>
            <Container className={classes.profile}>
              {/* persist form fields as long as the user has not successfully submitted the form */}
              {newProfile && (
                <FormPersistence
                  persistKey="profile-form-create"
                  shouldPersist={!(apiResponseObject && apiResponseObject.success)}
                  formValues={values}
                  setFormValues={setValues}
                />
              )}
              <form onSubmit={handleSubmit}>
                <div className={classes.titleWrapper}>
                  <Typography variant="h4" className={classes.profileTitle}>Coach Profile</Typography>
                  <div className={classes.buttons}>
                    <SaveCancelButtons {...{
                      isSubmitting, handleReset, handleSubmit, newProfile,
                    }}
                    />
                  </div>
                </div>
                {!isEmpty(errors) && <ErrorDisplay errors={errors} touched={touched}/>}
                <hr/>
                <Navigation/>
                <div className={classes.formWrapper}>
                  {/* Profile Form Fields */}
                  {profileSectionsConfig.map(({ id, label, Component }) => (
                    <Fragment key={`${id}-fragment`}>
                      <div id={id} className={classes.idLink} />
                      <ContentSection title={label}>
                        <Component
                          values={values}
                          touched={touched}
                          errors={errors}
                          handleChange={handleChange}
                          handleBlur={handleBlur}
                          setFieldValue={setFieldValue}
                          setValues={setValues}
                          slugError={slugError}
                          setSlugError={setSlugError}
                          newProfile={newProfile}
                        />
                      </ContentSection>
                    </Fragment>
                  ))}
                </div>
                <hr/>
                {/* Form action buttons */}
                {!isEmpty(errors) && <ErrorDisplay errors={errors} touched={touched}/>}
                <div className={classes.titleWrapper}>
                  <div className={classes.buttons}>
                    <SaveCancelButtons {...{
                      isSubmitting, handleReset, handleSubmit, newProfile,
                    }}
                    />
                  </div>
                </div>
              </form>
            </Container>
            <DialogAPIMessage
              message={apiResponseObject && apiResponseObject.message}
              success={apiResponseObject && apiResponseObject.success}
              open={openApiResponseDialog}
              handleClose={handleDialogClose}
              isCreate={newProfile}
              customSlug={values.customSlug}
            />
          </div>
        )}
      </Formik>
    </>
  )
}

ProfileForm.propTypes = {
  formFields: PropTypes.object,
  newProfile: PropTypes.bool,
}

export default ProfileForm
